import Swagger from "swagger-client";

import allStores from "../stores/StoreLoader";
import {
  ExecutionEnvironment,
  isServerComponent,
} from "../stores/InterfaceStore";
import { getConfig } from "../_configs/AppConfig";
import { getCookieFromString } from "../utils/StringUtilities";
import { cache } from "react";
import { reportError } from "../utils/errorReporting";

const { publicRuntimeConfig } = getConfig();

const createSingleton = cache((): Swagger => {
  const swag = {
    url: publicRuntimeConfig.OAS_API_SPEC_URL,
    usePromise: true,
    debug: false,
    requestInterceptor: (req) => {
      if (
        req.url === publicRuntimeConfig.API_SPEC_URL ||
        req.url === publicRuntimeConfig.OAS_API_SPEC_URL
      ) {
        // default Accept value in swagger-client 3.x is "application/json, "application/yaml", but Watson uses "application/openapi+json"
        req.userFetch = async (url) => {
          return await fetch(url, {
            headers: {
              Accept: "application/openapi+json",
            },
            credentials: "same-origin",
            cache: "force-cache",
            next: { revalidate: false },
          });
        };
      } else if (req.body && !req.headers["Content-Type"]) {
        // https://github.com/swagger-api/swagger-js/blob/master/docs/migration/migration-2-x-to-3-x.md#default-content-type-values
        req.headers["Content-Type"] = "application/json";
      }
    },
  };
  return new Swagger(swag);
});

function withDataCompat(client: Swagger): Swagger {
  // Swagger 2.0 includes request body params inside spec "parameters", but OpenAPI 3.0 includes them as a separate
  // entity. This mutates any requests via WatsonApi to add 'requestBody' option as documented https://github.com/swagger-api/swagger-js/blob/master/docs/usage/tags-interface.md#openapi-v3x
  const opHandler: ProxyHandler<object> = {
    get(target, operationId: string) {
      const fn = (target as Record<string, unknown>)[operationId];
      // TODO when OpenAPI schema feels stable, these error handlers can be removed
      if (typeof fn !== "function") {
        reportError(new Error("Invalid function: " + operationId + ""), {
          user_generated: false,
          error_source: "unhandled_rejection",
        });
        return fn;
      }
      return (params: Record<string, unknown> = {}, options = {}) => {
        const { data } = params;
        return (fn as Function)
          .call(
            target,
            params,
            data !== undefined ? { ...options, requestBody: data } : options,
          )
          .catch((e) => {
            reportError(e, {
              user_generated: false,
              error_source: "unhandled_rejection",
              failed_fetch: true,
            });
            throw e; // also allow upstream to handle errors
          });
      };
    },
  };

  return new Proxy(client, {
    get(target, prop) {
      if (prop === "apis") {
        return new Proxy((target as Record<string, unknown>).apis as object, {
          get(apisTarget, resource: string) {
            const resourceObj = (apisTarget as Record<string, unknown>)[
              resource
            ];
            if (!resourceObj || typeof resourceObj !== "object")
              return resourceObj;
            return new Proxy(resourceObj as object, opHandler);
          },
        });
      }
      return (target as Record<string, unknown>)[prop as string];
    },
  });
}

let swaggerSingleton: Swagger | null = null;

export default async function WatsonApi(token: string | null = null) {
  if (ExecutionEnvironment.canUseDOM) {
    token = getCookieFromString("token", document.cookie);
  } else if (isServerComponent) {
    const { cookies } = await import("next/headers");
    const c = await cookies();
    token = (c.get("token") as string | undefined) || null;
  }

  if (!swaggerSingleton || !ExecutionEnvironment.canUseDOM) {
    swaggerSingleton = createSingleton();
  }

  const client = await swaggerSingleton;

  if (!client.authorizations) {
    client.authorizations = {};
  }
  if (token) {
    client.authorizations["WatsonAuthentication"] = token;
  }
  client.authorizations["accept-language"] =
    allStores.i18nStore.currentOrgSupportedLocale;
  client.authorizations["AppContext"] = process.env.NEXT_PUBLIC_APP_CTX;

  return withDataCompat(client);
}
